import { motion } from '@/components/public/PublicMotion';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import { Heading } from '@/components/ui/heading';
import { Badge } from '@/components/ui/badge';
import {
  Cookie,
  Settings,
  BarChart2,
  Globe,
  Mail,
  ShieldCheck,
  BadgeCheck,
  RefreshCw,
  Building2,
  type LucideIcon,
} from 'lucide-react';

const SectionHeader = ({
  icon: Icon,
  number,
  title,
}: {
  icon: LucideIcon;
  number: string;
  title: string;
}) => (
  <Heading level={2} icon={Icon} className="mb-5">
    <span className="text-muted-foreground mr-2">{number}</span>
    {title}
  </Heading>
);

const technicalCookies = [
  {
    name: '_emovia_session',
    provider: 'Emovia',
    purpose: 'Gestione della sessione di navigazione',
    duration: 'Sessione',
    type: 'First-party',
  },
  {
    name: 'CookieConsent',
    provider: 'Cookiebot',
    purpose: 'Memorizzazione delle preferenze cookie',
    duration: '12 mesi',
    type: 'First-party',
  },
  {
    name: '__cf_bm',
    provider: 'Cloudflare',
    purpose: 'Protezione da bot e attività automatizzate',
    duration: '30 minuti',
    type: 'First-party',
  },
];

const analyticsCookies = [
  {
    name: 'ph_*',
    provider: 'PostHog (UE)',
    purpose: 'Analisi aggregata delle visite e delle interazioni',
    duration: '12 mesi',
    type: 'First-party',
  },
];

const thirdParties = [
  {
    provider: 'PostHog Inc.',
    purpose: 'Analisi statistica',
    privacyUrl: 'https://posthog.com/privacy',
    privacyLabel: 'posthog.com/privacy',
  },
  {
    provider: 'Cookiebot (Usercentrics)',
    purpose: 'Gestione consenso',
    privacyUrl: 'https://www.cookiebot.com/goto/privacy-policy',
    privacyLabel: 'cookiebot.com/goto/privacy-policy',
  },
];

const CookiePolicy = () => {
  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Cookie Policy"
        description="Informazioni sui cookie utilizzati dal sito Emovia, sulle finalità e sulle modalità di gestione del consenso."
        path="/cookie-policy"
      />

      {/* Hero */}
      <section className="py-20 md:py-28 bg-blue-lighter/30">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }} className="mb-6">
              <Badge variant="outline" className="bg-primary/10 border-primary/20 text-primary/80 rounded-full px-4 py-1.5">
                Ultimo aggiornamento: 25 marzo 2026
              </Badge>
            </motion.div>
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={1} font="display" className="text-primary mb-3">
                Informativa sui Cookie
              </Heading>
            </motion.div>
            <motion.p variants={fadeInUp} transition={{ duration: 0.5 }} className="text-lg text-muted-foreground">
              ai sensi dell&apos;art. 122 del D.Lgs. 196/2003 e delle Linee Guida del Garante per la Protezione dei Dati Personali del 10 giugno 2021
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">

            {/* 1. Titolare */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Building2} number="1." title="Titolare del trattamento" />
              <div className="text-muted-foreground space-y-1">
                <p className="text-foreground">Emovia di Daniele Di Bernardo</p>
                <p>Viale Sicilia 31, 27100 Pavia (PV), Italia</p>
                <p>P.IVA: IT03023520186</p>
                <a
                  href="mailto:privacy@emovia.com"
                  aria-label="Invia email a privacy@emovia.com"
                  className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline mt-2"
                >
                  <Mail className="h-4 w-4" aria-hidden />
                  privacy@emovia.com
                </a>
              </div>
            </motion.div>

            {/* 2. Cosa sono i cookie */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Cookie} number="2." title="Cosa sono i cookie" />
              <p className="text-muted-foreground">
                I cookie sono piccoli file di testo che i siti web memorizzano sul tuo dispositivo
                durante la navigazione. Servono a garantire il funzionamento del sito, ricordare le
                tue preferenze e raccogliere informazioni statistiche sull&apos;utilizzo.
              </p>
            </motion.div>

            {/* 3. Cookie utilizzati */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Settings} number="3." title="Cookie utilizzati dal Sito" />

              {/* 3.1 Tecnici */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden />
                  <Heading level={3}>
                    <span className="text-muted-foreground mr-1.5">3.1</span>
                    Cookie tecnici (non richiedono consenso)
                  </Heading>
                </div>
                <p className="text-muted-foreground mb-4">
                  Questi cookie sono strettamente necessari al funzionamento del Sito. Senza di essi,
                  alcune funzionalità non sarebbero disponibili.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-muted/40 text-left border-b border-border">
                        <th scope="col" className="px-4 py-3 text-foreground">Nome</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Fornitore</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Finalità</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Durata</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Tipo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {technicalCookies.map(({ name, provider, purpose, duration, type }) => (
                        <tr key={name} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-foreground font-mono text-xs">{name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{provider}</td>
                          <td className="px-4 py-3 text-muted-foreground">{purpose}</td>
                          <td className="px-4 py-3 text-muted-foreground">{duration}</td>
                          <td className="px-4 py-3 text-muted-foreground">{type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 3.2 Analytics */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart2 className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden />
                  <Heading level={3}>
                    <span className="text-muted-foreground mr-1.5">3.2</span>
                    Cookie di analisi statistica (richiedono consenso)
                  </Heading>
                </div>
                <p className="text-muted-foreground mb-4">
                  Questi cookie raccolgono informazioni in forma aggregata sull&apos;utilizzo del Sito
                  per finalità statistiche.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-muted/40 text-left border-b border-border">
                        <th scope="col" className="px-4 py-3 text-foreground">Nome</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Fornitore</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Finalità</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Durata</th>
                        <th scope="col" className="px-4 py-3 text-foreground">Tipo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {analyticsCookies.map(({ name, provider, purpose, duration, type }) => (
                        <tr key={name} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-foreground font-mono text-xs">{name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{provider}</td>
                          <td className="px-4 py-3 text-muted-foreground">{purpose}</td>
                          <td className="px-4 py-3 text-muted-foreground">{duration}</td>
                          <td className="px-4 py-3 text-muted-foreground">{type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-muted-foreground mt-4 text-sm">
                  I cookie di analisi vengono installati solo dopo aver ottenuto il tuo consenso
                  tramite il banner cookie.
                </p>
              </div>

              {/* 3.3 Profilazione */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <BadgeCheck className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden />
                  <Heading level={3}>
                    <span className="text-muted-foreground mr-1.5">3.3</span>
                    Cookie di profilazione e marketing
                  </Heading>
                </div>
                <p className="text-muted-foreground">
                  Il Sito non utilizza cookie di profilazione né cookie per finalità di marketing.
                </p>
              </div>
            </motion.div>

            {/* 4. Gestione consenso */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={ShieldCheck} number="4." title="Gestione del consenso" />
              <p className="text-muted-foreground mb-4">
                Il consenso per i cookie non tecnici viene raccolto tramite il banner di CookieBot
                (Usercentrics), visualizzato al primo accesso al Sito.
              </p>
              <p className="text-muted-foreground mb-3">Puoi in qualsiasi momento:</p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    Modificare le tue preferenze tramite il link{' '}
                    <span className="text-foreground">&ldquo;Gestisci cookie&rdquo;</span> presente
                    nel footer del Sito.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    Revocare il consenso prestato, senza che ciò pregiudichi la liceità dei
                    trattamenti effettuati prima della revoca.
                  </span>
                </li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Il rifiuto dei cookie non tecnici non compromette la navigazione del Sito.
              </p>
            </motion.div>

            {/* 5. Cookie terze parti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Globe} number="5." title="Cookie di terze parti" />
              <p className="text-muted-foreground mb-6">
                Il Sito si avvale dei seguenti fornitori terzi che possono installare cookie:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Fornitore</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Finalità</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Informativa privacy</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {thirdParties.map(({ provider, purpose, privacyUrl, privacyLabel }) => (
                      <tr key={provider} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{provider}</td>
                        <td className="px-4 py-3 text-muted-foreground">{purpose}</td>
                        <td className="px-4 py-3">
                          <a
                            href={privacyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline-offset-4 hover:underline"
                          >
                            {privacyLabel}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* 6. Diritti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={BadgeCheck} number="6." title="I tuoi diritti" />
              <p className="text-muted-foreground mb-4">
                Puoi esercitare i diritti previsti dagli artt. 15-21 del GDPR scrivendo a{' '}
                <a
                  href="mailto:privacy@emovia.com"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  privacy@emovia.com
                </a>
                . Per maggiori dettagli, consulta la nostra{' '}
                <a
                  href="/privacy-policy"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <p className="text-muted-foreground">
                Hai inoltre il diritto di proporre reclamo al Garante per la Protezione dei Dati
                Personali (
                <a
                  href="https://www.garanteprivacy.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  www.garanteprivacy.it
                </a>
                ).
              </p>
            </motion.div>

            {/* 7. Modifiche */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10"
            >
              <SectionHeader icon={RefreshCw} number="7." title="Modifiche" />
              <p className="text-muted-foreground">
                Il Titolare si riserva di aggiornare la presente informativa. La data di ultimo
                aggiornamento è indicata in cima al documento.
              </p>
            </motion.div>

          </div>

          {/* Contatti privacy */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto mt-10"
          >
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
              <Mail className="h-7 w-7 text-primary mx-auto mb-4" aria-hidden />
              <Heading level={3} className="mb-2">Hai domande sui cookie?</Heading>
              <p className="text-muted-foreground mb-5">
                Scrivici. Ti rispondiamo entro 30 giorni.
              </p>
              <a
                href="mailto:privacy@emovia.com"
                aria-label="Invia email a privacy@emovia.com"
                className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
              >
                <Mail className="h-4 w-4" aria-hidden />
                privacy@emovia.com
              </a>
            </div>
          </motion.div>

        </div>
      </section>
    </PublicLayout>
  );
};

export default CookiePolicy;
